import { BASE_URL, createTimeoutSignal } from "../api";
import { AI_CLIENT_CHANNEL } from "../utils/aiAssistantTypes";

export const AI_ERROR_CODES = {
  RATE_LIMIT: "RATE_LIMIT",
  UNAVAILABLE: "UNAVAILABLE",
  NETWORK: "NETWORK",
  VALIDATION: "VALIDATION",
  REQUEST_FAILED: "REQUEST_FAILED",
};

export class AiAssistantApiError extends Error {
  /** @param {string} code @param {string} message @param {number} [status] */
  constructor(code, message, status) {
    super(message);
    this.name = "AiAssistantApiError";
    this.code = code;
    this.status = status;
  }
}

function getAuthToken() {
  try {
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
}

/** @param {Record<string, string>} [extra] @param {boolean} [jsonBody] */
function buildAiHeaders(extra = {}, jsonBody = true) {
  const headers = {
    Accept: "application/json",
    "X-Client-Channel": AI_CLIENT_CHANNEL,
    ...extra,
  };
  if (jsonBody) headers["Content-Type"] = "application/json";
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseJsonResponse(res) {
  const raw = await res.text();
  try {
    return raw ? JSON.parse(raw) : {};
  } catch {
    throw new AiAssistantApiError(
      AI_ERROR_CODES.REQUEST_FAILED,
      `Invalid response (${res.status})`,
      res.status
    );
  }
}

function mapFetchError(err, status) {
  if (err instanceof AiAssistantApiError) return err;
  if (err?.name === "AbortError") {
    return new AiAssistantApiError(
      AI_ERROR_CODES.NETWORK,
      "Check your connection and try again."
    );
  }
  if (status === 429) {
    return new AiAssistantApiError(
      AI_ERROR_CODES.RATE_LIMIT,
      "Too many requests — please wait a moment.",
      429
    );
  }
  if (status === 503) {
    return new AiAssistantApiError(
      AI_ERROR_CODES.UNAVAILABLE,
      "Assistant temporarily unavailable.",
      503
    );
  }
  if (status === 422) {
    return new AiAssistantApiError(
      AI_ERROR_CODES.VALIDATION,
      "Something went wrong with that request. Please try again.",
      422
    );
  }
  if (err instanceof TypeError) {
    return new AiAssistantApiError(
      AI_ERROR_CODES.NETWORK,
      "Check your connection and try again."
    );
  }
  return new AiAssistantApiError(
    AI_ERROR_CODES.REQUEST_FAILED,
    err?.message || "Something went wrong. Please try again.",
    status
  );
}

/**
 * @param {string} message
 * @param {string} sessionId
 * @param {import('../utils/aiAssistantTypes').AiChatContext} context
 */
export async function sendAiMessage(message, sessionId, context) {
  const url = `${BASE_URL}/ai-assistant/message`;
  let res;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: buildAiHeaders(),
      body: JSON.stringify({
        message,
        session_id: sessionId,
        channel: AI_CLIENT_CHANNEL,
        context,
      }),
      signal: createTimeoutSignal(30000),
    });
  } catch (err) {
    throw mapFetchError(err);
  }

  const data = await parseJsonResponse(res);

  if (!res.ok) {
    const errMessage =
      res.status === 422
        ? "Something went wrong with that request. Please try again."
        : data?.message || `Request failed (${res.status})`;
    throw mapFetchError(new Error(errMessage), res.status);
  }

  return data;
}

/** @param {string} sessionId */
export async function fetchAiSessionMessages(sessionId) {
  if (!sessionId) return [];
  const url = `${BASE_URL}/ai-assistant/sessions/${encodeURIComponent(sessionId)}/messages`;
  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: buildAiHeaders({}, false),
      signal: createTimeoutSignal(15000),
    });
  } catch (err) {
    throw mapFetchError(err);
  }

  if (res.status === 404) return [];

  const data = await parseJsonResponse(res);

  if (!res.ok) {
    throw mapFetchError(
      new Error(data?.message || `Request failed (${res.status})`),
      res.status
    );
  }

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.messages)) return data.messages;
  return [];
}

/**
 * Brain v2 session payload including optional session_summary.
 * @param {string} sessionId
 * @returns {Promise<{ messages: unknown[], sessionSummary: string|null }>}
 */
export async function fetchAiSessionPayload(sessionId) {
  if (!sessionId) return { messages: [], sessionSummary: null };
  const url = `${BASE_URL}/ai-assistant/sessions/${encodeURIComponent(sessionId)}/messages`;
  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: buildAiHeaders({}, false),
      signal: createTimeoutSignal(15000),
    });
  } catch (err) {
    throw mapFetchError(err);
  }

  if (res.status === 404) return { messages: [], sessionSummary: null };

  const data = await parseJsonResponse(res);

  if (!res.ok) {
    throw mapFetchError(
      new Error(data?.message || `Request failed (${res.status})`),
      res.status
    );
  }

  if (Array.isArray(data)) {
    return { messages: data, sessionSummary: null };
  }

  const messages = Array.isArray(data?.messages)
    ? data.messages
    : Array.isArray(data?.data)
      ? data.data
      : [];

  const sessionSummary =
    typeof data?.session_summary === "string" && data.session_summary.trim()
      ? data.session_summary.trim()
      : null;

  return { messages, sessionSummary };
}

/**
 * @param {string} type e.g. apartment_buy
 * @param {string} city
 */
export async function fetchAiProjectsPrefetch(type, city) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (city) params.set("city", city);
  const qs = params.toString();
  const url = `${BASE_URL}/ai-assistant/projects${qs ? `?${qs}` : ""}`;
  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      headers: buildAiHeaders({}, false),
      signal: createTimeoutSignal(15000),
    });
  } catch (err) {
    throw mapFetchError(err);
  }

  const data = await parseJsonResponse(res);

  if (!res.ok) {
    throw mapFetchError(
      new Error(data?.message || `Request failed (${res.status})`),
      res.status
    );
  }

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.projects)) return data.projects;
  return [];
}
